import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchSchedule } from '@apis/schedule';
import { scheduleKey } from '@constants/queryKeys';
import type { ISchedulerData, SiteId } from '@interfaces/IScheduler';
import { useSchedulerActions } from './useSchedulerStore';

/**
 * Re-read the schedule after a write that changed the PLAN server-side (a
 * commit's fills, a finish's dates) and rebase the store onto it.
 *
 * The store is bootstrapped once per schedule and a refetch does not
 * re-bootstrap it, so a bare invalidate would move the query cache — the
 * rebase BASELINE — past the store without moving the store: the next reload
 * would read the server's change as a user edit, and Save would write the old
 * value back over it. So the re-read is a three-way rebase against the cache as
 * it was before the write, which also keeps any unsaved Gantt edits.
 *
 * Resolves with the fresh schedule, or `null` when the read failed — which
 * leaves cache and store both where they were, so the stale probe's reload
 * still rebases against the right baseline.
 */
export function usePullPlan(siteId: SiteId): (scheduleNumber: string) => Promise<ISchedulerData | null> {
  const qc = useQueryClient();
  const { rebaseOnFresh, clearHistory } = useSchedulerActions();

  return useCallback(
    async (scheduleNumber: string) => {
      const queryKey = scheduleKey(siteId, scheduleNumber);
      const baseline = qc.getQueryData<ISchedulerData>(queryKey);
      try {
        const fresh = await qc.fetchQuery({
          queryKey,
          queryFn: ({ signal }) => fetchSchedule(siteId, scheduleNumber, signal),
          staleTime: 0,
        });
        rebaseOnFresh({ siteId, fresh: { ...fresh, siteId }, ...(baseline ? { baseline: { ...baseline, siteId } } : {}) });
        // History patches reference the pre-rebase copy — same as a reload.
        clearHistory();
        return fresh;
      } catch {
        // Nothing moved; the stale banner offers the reload.
        return null;
      }
    },
    [qc, siteId, rebaseOnFresh, clearHistory],
  );
}
